import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Descriptions, Divider, Select, Radio, InputNumber, Input, Button, Tag, List, Typography, message } from 'antd';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { getPatientCreditBalance, getPatientCreditHistory, adjustPatientCredit, getPatientPurchaseHistory } from '../redux/adminSlice';

const currency = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' });

const formatDate = (value) => {
  const m = moment(value);
  return m.isValid() && m.year() > 1 ? m.format('MMM D, YYYY') : '';
};

const PatientInformationView = ({ isOpen, setOpen, account, patientList }) => {
  const [patientData, setPatientData] = useState(null);

  const dispatch = useDispatch();

  const [creditBalance, setCreditBalance] = useState([]);
  const [creditBalanceLoading, setCreditBalanceLoading] = useState(false);
  const [creditHistory, setCreditHistory] = useState([]);
  const [purchaseHistory, setPurchaseHistory] = useState(null);
  const [creditAdjustmentLoading, setCreditAdjustmentLoading] = useState(false);

  const [creditTypeId, setCreditTypeId] = useState(null);
  const [isCredit, setIsCredit] = useState(true);
  const [amount, setAmount] = useState(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!account || !Array.isArray(patientList)) return;

    const selected = patientList.find(p => p.account === account);
    setPatientData(selected);
  }, [account, patientList]);

  const fetchCreditBalance = useCallback(() => {
    setCreditBalanceLoading(true);
    dispatch(getPatientCreditBalance(account))
      .then((response) => {
        if (getPatientCreditBalance.fulfilled.match(response)) {
    
          const payload = response.payload;
          const rows = payload?.data ?? payload?.Data ?? payload;
          setCreditBalance(Array.isArray(rows) ? rows : []);
        } else {
          setCreditBalance([]);
        }
      })
      .finally(() => setCreditBalanceLoading(false));
  }, [dispatch, account]);

  const fetchCreditHistory = useCallback(() => {
    dispatch(getPatientCreditHistory(account)).then((response) => {
      if (getPatientCreditHistory.fulfilled.match(response)) {
        setCreditHistory(Array.isArray(response.payload) ? response.payload : []);
      } else {
        setCreditHistory([]);
      }
    });
  }, [dispatch, account]);

  const fetchPurchaseHistory = useCallback(() => {
    dispatch(getPatientPurchaseHistory({ userRef: account })).then((response) => {
      setPurchaseHistory(
        getPatientPurchaseHistory.fulfilled.match(response) && Array.isArray(response.payload?.data)
          ? response.payload.data
          : null,
      );
    });
  }, [dispatch, account]);

  useEffect(() => {
    if (isOpen === 'Credits' && account) {
      fetchCreditBalance();
      fetchCreditHistory();
      fetchPurchaseHistory();
      setCreditTypeId(null);
      setIsCredit(true);
      setAmount(null);
      setReason('');
    }
  }, [isOpen, account, fetchCreditBalance, fetchCreditHistory, fetchPurchaseHistory]);

  const handleAdjustCredit = () => {
    if (!creditTypeId) return message.error('Select a credit type.');
    if (!amount || amount <= 0) return message.error('Amount must be greater than zero.');
    if (!reason.trim()) return message.error('Reason is required.');

    setCreditAdjustmentLoading(true);
    dispatch(adjustPatientCredit({
      userRef: account,
      creditTypeId,
      isCredit,
      value: amount,
      reason: reason.trim(),
    }))
      .then((response) => {
        if (adjustPatientCredit.fulfilled.match(response)) {
          const { success, message: msg } = response.payload || {};
          if (success) {
            message.success(msg || 'Credit adjusted successfully.');
            setAmount(null);
            setReason('');
            fetchCreditBalance();
            fetchCreditHistory();
          } else {
            message.error(msg || 'Failed to adjust credit.');
          }
        } else {
          message.error(response?.payload?.message || 'Failed to adjust credit.');
        }
      })
      .finally(() => setCreditAdjustmentLoading(false));
  };

  const isCreditsView = isOpen === 'Credits';

  return (
    <Modal
      title={isCreditsView ? 'Manage Credits' : 'Patient Application Details'}
      open={isOpen === 'Application' || isCreditsView}
      onCancel={() => setOpen('')}
      footer={null}
      width={700}
    >
      {!patientData ? (
        <p>Patient not found.</p>
      ) : isCreditsView ? (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {creditBalanceLoading ? (
              <Typography.Text type="secondary">Loading balances…</Typography.Text>
            ) : creditBalance.length === 0 ? (
              <Typography.Text type="secondary">No credit balance data available.</Typography.Text>
            ) : (
              creditBalance.map((row) => (
                <Tag key={row.creditTypeId ?? row.CreditTypeId} color="blue">
                  {(row.name ?? row.Name)}: {row.balance ?? row.Balance ?? 0}
                </Tag>
              ))
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
            <Select
              placeholder="Credit type"
              style={{ minWidth: 180 }}
              value={creditTypeId}
              onChange={setCreditTypeId}
              options={creditBalance.map((row) => ({
                value: row.creditTypeId ?? row.CreditTypeId,
                label: row.name ?? row.Name,
              }))}
            />
            <Radio.Group value={isCredit} onChange={(e) => setIsCredit(e.target.value)}>
              <Radio.Button value={true}>Add</Radio.Button>
              <Radio.Button value={false}>Deduct</Radio.Button>
            </Radio.Group>
            <InputNumber min={0.01} placeholder="Amount" value={amount} onChange={setAmount} />
          </div>
          <Input.TextArea
            placeholder="Reason (required)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            style={{ marginBottom: 12 }}
          />
          <Button type="primary" loading={creditAdjustmentLoading} onClick={handleAdjustCredit}>
            Apply adjustment
          </Button>

          <Divider>Recent credit history</Divider>
          <List
            size="small"
            dataSource={creditHistory.slice(0, 5)}
            locale={{ emptyText: 'No credit history yet.' }}
            renderItem={(row) => (
              <List.Item>
                <Typography.Text>
                  {(row.isCreditorDebit ?? row.IsCreditorDebit) ? '+' : '-'}
                  {row.value ?? row.Value} {row.creditTypeName ?? row.CreditTypeName} —{' '}
                  {row.reason ?? row.Reason} ({(row.adminName ?? row.AdminName) || 'system'})
                </Typography.Text>
              </List.Item>
            )}
          />

          <Divider>Purchase history</Divider>
          <List
            size="small"
            dataSource={purchaseHistory ?? []}
            locale={{
              emptyText: purchaseHistory ? 'No purchases yet.' : "Purchase history isn't available yet.",
            }}
            renderItem={(row) => (
              <List.Item>
                <Typography.Text>
                  {row.subscriptionName} — {currency.format(row.totalAmount ?? 0)}
                  {formatDate(row.paidOn || row.startDate) && ` · ${formatDate(row.paidOn || row.startDate)}`}
                </Typography.Text>
              </List.Item>
            )}
          />
        </>
      ) : (
        <Descriptions bordered column={1}>
          <Descriptions.Item label="First Name">{patientData.firstName}</Descriptions.Item>
          <Descriptions.Item label="Last Name">{patientData.lastName}</Descriptions.Item>
          <Descriptions.Item label="Account #">{patientData.account}</Descriptions.Item>
          <Descriptions.Item label="DOB">{patientData.dob}</Descriptions.Item>
          <Descriptions.Item label="Email ">{patientData.email}</Descriptions.Item>
          <Descriptions.Item label="Role">{patientData.role}</Descriptions.Item>
          <Descriptions.Item label="Activated Status">{patientData.activated}</Descriptions.Item>
          <Descriptions.Item label="Accepting Patients">{patientData.acceptingPatients}</Descriptions.Item>
          {/* Add more fields if your API gives more data */}
        </Descriptions>
      )}
    </Modal>
  );
};

export default PatientInformationView;
